import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown, assets } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import { Toggle } from '@round/whitelabel-ui';
import copy from 'copy-to-clipboard';
import { showNotification } from 'helpers';
import { creatorbase } from '@round/api';
import styles from './ShareDropdown.module.css';
import CreateLinkModal from '../CreateLinkModal/CreateLinkModal';
import { formatDate } from '../../../../../../helpers';
import DeleteReportModal from './DeleteReportModal';

const {
    Eye: EyeIcon,
    EyeInactive: EyeIconInactive,
    ChevronRight: ChevronRightIcon,
    Plus: PlusIcon,
    Copy: CopyIcon,
    Edit: EditIcon,
    Trash: TrashIcon,
} = assets;

interface ShareDropdownProps {
    isInitialized: boolean;
    onCreateLink: () => void;
    publicReports: creatorbase.PublicReportsListItem[];
    onDeleteReport: (publicReportId: string) => Promise<void>;
    onUpdateReport?: (updatedReport: creatorbase.PublicReportsListItem) => void;
    reportId: string;
    onPublishReport: (payload: creatorbase.PostPublicReportData) => Promise<void>;
}

const ShareDropdown: React.FC<ShareDropdownProps> = ({
    isInitialized,
    onCreateLink,
    publicReports,
    onDeleteReport,
    onUpdateReport,
    reportId,
    onPublishReport,
}) => {
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [editingRows, setEditingRows] = useState<Set<string>>(new Set());
    const [editValues, setEditValues] = useState<
        Record<string, { include_cost: boolean; include_benchmark: boolean; include_ad_codes: boolean }>
    >({});
    const [isCreateLinkModalOpen, setIsCreateLinkModalOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState<{
        isOpen: boolean;
        reportId: string;
        reportName: string;
    }>({
        isOpen: false,
        reportId: '',
        reportName: '',
    });

    // Initialize edit values when public reports change
    useEffect(() => {
        if (publicReports.length > 0) {
            const initialEditValues: Record<
                string,
                { include_cost: boolean; include_benchmark: boolean; include_ad_codes: boolean }
            > = {};
            publicReports.forEach((report) => {
                initialEditValues[report.id] = {
                    include_cost: report.include_cost || false,
                    include_benchmark: report.include_benchmark ?? true,
                    include_ad_codes: report.include_ad_codes || false,
                };
            });
            setEditValues(initialEditValues);
        } else {
            setEditValues({});
        }
    }, [publicReports]);

    const resetDropdownState = useCallback(() => {
        setExpandedRows(new Set());
        setEditingRows(new Set());
        // Reset edit values to original values
        if (publicReports.length > 0) {
            const initialEditValues: Record<
                string,
                { include_cost: boolean; include_benchmark: boolean; include_ad_codes: boolean }
            > = {};
            publicReports.forEach((report) => {
                initialEditValues[report.id] = {
                    include_cost: report.include_cost || false,
                    include_benchmark: report.include_benchmark ?? true,
                    include_ad_codes: report.include_ad_codes || false,
                };
            });
            setEditValues(initialEditValues);
        } else {
            setEditValues({});
        }
    }, [publicReports]);

    // Reset state when dropdown closes
    useEffect(() => {
        if (!isDropdownOpen) {
            resetDropdownState();
        }
    }, [isDropdownOpen, resetDropdownState]);

    // Helper functions for row management
    const toggleExpanded = (reportId: string) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(reportId)) {
            newExpanded.delete(reportId);
        } else {
            newExpanded.add(reportId);
        }
        setExpandedRows(newExpanded);
    };

    const toggleEditMode = (reportId: string) => {
        const newEditing = new Set(editingRows);
        if (newEditing.has(reportId)) {
            newEditing.delete(reportId);
        } else {
            newEditing.add(reportId);
            // Initialize edit values with current report data
            const report = publicReports.find((r) => r.id === reportId);
            if (report) {
                setEditValues((prev) => ({
                    ...prev,
                    [reportId]: {
                        include_cost: report.include_cost,
                        include_benchmark: report.include_benchmark,
                        include_ad_codes: report.include_ad_codes,
                    },
                }));
            }
        }
        setEditingRows(newEditing);
    };

    const handleEditValueChange = (
        reportId: string,
        field: 'include_cost' | 'include_benchmark' | 'include_ad_codes',
        value: boolean
    ) => {
        setEditValues((prev) => ({
            ...prev,
            [reportId]: {
                ...prev[reportId],
                [field]: value,
            },
        }));
    };

    const handleSaveEdit = async (reportId: string) => {
        try {
            // Get the current edit values for this report
            const editData = editValues[reportId];
            if (!editData) {
                showNotification('No changes to save', 'info');
                return;
            }
            toggleEditMode(reportId);

            // Call the parent callback to refresh the data
            if (onUpdateReport) {
                const currentReport = publicReports.find((r) => r.id === reportId);
                if (currentReport) {
                    const updatedReport = { ...currentReport, ...editData };
                    onUpdateReport(updatedReport);
                }
            }
        } catch (error) {
            showNotification('Failed to update shareable link', 'error');
        }
    };

    const handleCopyLink = (reportId: string, setIsOpen: (isOpen: boolean) => void) => {
        const link = `${window.location.origin}/c/public-reports/${reportId}`;
        copy(link);
        showNotification('Link copied to clipboard!', 'info');
        setIsOpen(false);
    };

    const handleDeleteClick = (reportId: string, reportName: string, setIsOpen: (open: boolean) => void) => {
        // Close the dropdown first, then open the modal
        setIsOpen(false);
        // Use setTimeout to ensure dropdown closes before modal opens
        setTimeout(() => {
            setDeleteConfirmation({
                isOpen: true,
                reportId,
                reportName,
            });
        }, 100);
    };

    const handleCancelDelete = () => {
        setDeleteConfirmation({
            isOpen: false,
            reportId: '',
            reportName: '',
        });
    };

    const handleToggleActiveState = async (reportId: string) => {
        try {
            const currentReport = publicReports.find((r) => r.id === reportId);
            if (currentReport && onUpdateReport) {
                const updatedReport = { ...currentReport, active: !currentReport.active };
                await onUpdateReport(updatedReport);
            }
        } catch (error) {
            showNotification('Failed to update shareable link status', 'error');
        }
    };

    const handleCreateLinkSuccess = () => {
        // Close the modal and refresh the data
        setIsCreateLinkModalOpen(false);
        // Call the parent's onCreateLink to refresh data
        onCreateLink();
    };

    return (
        <div className="relative">
            <Dropdown>
                {({ isOpen, setIsOpen, setAnchorElement }) => {
                    // Update dropdown state and reset when closing
                    if (isOpen !== isDropdownOpen) {
                        setIsDropdownOpen(isOpen);
                    }

                    return (
                        <>
                            <Button
                                appearance="primary"
                                className={styles.shareButton}
                                disabled={!isInitialized}
                                ref={setAnchorElement}
                                onClick={() => setIsOpen((isOpen) => !isOpen)}
                            >
                                Share{publicReports.length > 0 && ` (${publicReports.length})`}
                            </Button>

                            <Dropdown.Menu className={styles.customDropdownLeft}>
                                <Button
                                    appearance="outline"
                                    className={styles.dropdownButton}
                                    onClick={() => {
                                        setIsCreateLinkModalOpen(true);
                                        setIsOpen(false);
                                    }}
                                >
                                    <PlusIcon className={styles.dropdownIcon} />
                                    Create Link
                                </Button>

                                {publicReports.length > 0 && (
                                    <div>
                                        <div className={styles.dropdownHeader}>Your Shareable Links</div>
                                        {publicReports.map((report) => (
                                            <div key={report.id} className={styles.reportContainer}>
                                                <div
                                                    className={styles.reportButtonRow}
                                                    onClick={() => toggleExpanded(report.id)}
                                                >
                                                    <ChevronRightIcon
                                                        className={`${
                                                            expandedRows.has(report.id) ? styles.rotate90 : ''
                                                        }`}
                                                    />
                                                    <div className={styles.contentArea}>
                                                        <div className={styles.reportNameContainer}>
                                                            <div className={styles.reportName}>{report.name}</div>
                                                            <div
                                                                className={`${
                                                                    report.active ? styles.greenDot : styles.grayDot
                                                                }`}
                                                            ></div>
                                                        </div>
                                                        <div className={styles.reportDate}>
                                                            {report.created_at ? (
                                                                <div>
                                                                    <div>{formatDate(report.created_at)}</div>
                                                                </div>
                                                            ) : (
                                                                'No date'
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={styles.buttonContainer}>
                                                        <Button
                                                            appearance="ghost"
                                                            className={styles.copyButton}
                                                            onClick={() => handleCopyLink(report.id, setIsOpen)}
                                                        >
                                                            <CopyIcon className={styles.copyIcon} />
                                                        </Button>
                                                    </div>
                                                </div>

                                                {/* Expanded Content */}
                                                {expandedRows.has(report.id) && (
                                                    <div className={styles.expandedContent}>
                                                        <div className={styles.toggleCard}>
                                                            <div className={styles.toggleHeader}>
                                                                <div className={styles.toggleLabel}>
                                                                    <div
                                                                        className={
                                                                            editingRows.has(report.id)
                                                                                ? editValues[report.id]?.include_cost
                                                                                    ? styles.greenDot
                                                                                    : styles.grayDot
                                                                                : report.include_cost
                                                                                  ? styles.greenDot
                                                                                  : styles.grayDot
                                                                        }
                                                                    ></div>
                                                                    <span className={styles.toggleLabelText}>
                                                                        Costs
                                                                    </span>
                                                                </div>
                                                                {editingRows.has(report.id) ? (
                                                                    <Toggle
                                                                        value={
                                                                            editValues[report.id]?.include_cost || false
                                                                        }
                                                                        onChange={(value) =>
                                                                            handleEditValueChange(
                                                                                report.id,
                                                                                'include_cost',
                                                                                value
                                                                            )
                                                                        }
                                                                        ariaLabel="Include cost data"
                                                                    />
                                                                ) : (
                                                                    <div className={styles.toggleStatus}>
                                                                        {editValues[report.id]?.include_cost
                                                                            ? 'Shown'
                                                                            : 'Hidden'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className={styles.toggleCard}>
                                                            <div className={styles.toggleHeader}>
                                                                <div className={styles.toggleLabel}>
                                                                    <div
                                                                        className={
                                                                            editValues[report.id]?.include_benchmark ===
                                                                            false
                                                                                ? styles.grayDot
                                                                                : styles.greenDot
                                                                        }
                                                                    ></div>
                                                                    <span className={styles.toggleLabelText}>
                                                                        Benchmarks
                                                                    </span>
                                                                </div>
                                                                {editingRows.has(report.id) ? (
                                                                    <Toggle
                                                                        value={
                                                                            editValues[report.id]?.include_benchmark ??
                                                                            true
                                                                        }
                                                                        onChange={(value) =>
                                                                            handleEditValueChange(
                                                                                report.id,
                                                                                'include_benchmark',
                                                                                value
                                                                            )
                                                                        }
                                                                        ariaLabel="Include benchmark data"
                                                                    />
                                                                ) : (
                                                                    <div className={styles.toggleStatus}>
                                                                        {editValues[report.id]?.include_benchmark
                                                                            ? 'Shown'
                                                                            : 'Hidden'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className={styles.toggleCard}>
                                                            <div className={styles.toggleHeader}>
                                                                <div className={styles.toggleLabel}>
                                                                    <div
                                                                        className={
                                                                            editingRows.has(report.id)
                                                                                ? editValues[report.id]
                                                                                      ?.include_ad_codes
                                                                                    ? styles.greenDot
                                                                                    : styles.grayDot
                                                                                : report.include_ad_codes
                                                                                  ? styles.greenDot
                                                                                  : styles.grayDot
                                                                        }
                                                                    ></div>
                                                                    <span className={styles.toggleLabelText}>
                                                                        Ad codes
                                                                    </span>
                                                                </div>
                                                                {editingRows.has(report.id) ? (
                                                                    <Toggle
                                                                        value={
                                                                            editValues[report.id]?.include_ad_codes ||
                                                                            false
                                                                        }
                                                                        onChange={(value) =>
                                                                            handleEditValueChange(
                                                                                report.id,
                                                                                'include_ad_codes',
                                                                                value
                                                                            )
                                                                        }
                                                                        ariaLabel="Include ad codes"
                                                                    />
                                                                ) : (
                                                                    <div className={styles.toggleStatus}>
                                                                        {editValues[report.id]?.include_ad_codes
                                                                            ? 'Shown'
                                                                            : 'Hidden'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className={styles.actionBar}>
                                                            {editingRows.has(report.id) ? (
                                                                <Button
                                                                    appearance="ghost"
                                                                    className={styles.saveButton}
                                                                    onClick={() => handleSaveEdit(report.id)}
                                                                >
                                                                    Save
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    appearance="ghost"
                                                                    className={styles.editButton}
                                                                    onClick={() => toggleEditMode(report.id)}
                                                                >
                                                                    <EditIcon className={styles.editIcon} />
                                                                    Edit
                                                                </Button>
                                                            )}
                                                            <div className={styles.statusButtons}>
                                                                <Button
                                                                    appearance="ghost"
                                                                    className={`${styles.publishedButton} ${
                                                                        report.active
                                                                            ? styles.activeButton
                                                                            : styles.inactiveButton
                                                                    }`}
                                                                    onClick={() => handleToggleActiveState(report.id)}
                                                                >
                                                                    {report.active ? (
                                                                        <EyeIcon className={styles.eyeIcon} />
                                                                    ) : (
                                                                        <EyeIconInactive className={styles.eyeIcon} />
                                                                    )}
                                                                    {report.active ? 'Published' : 'Unpublished'}
                                                                </Button>
                                                                <Button
                                                                    appearance="ghost"
                                                                    className={styles.deleteButton}
                                                                    onClick={() =>
                                                                        handleDeleteClick(
                                                                            report.id,
                                                                            report.name,
                                                                            setIsOpen
                                                                        )
                                                                    }
                                                                >
                                                                    <TrashIcon className={styles.trashIcon} />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Dropdown.Menu>
                        </>
                    );
                }}
            </Dropdown>

            <CreateLinkModal
                isOpen={isCreateLinkModalOpen}
                onClose={() => setIsCreateLinkModalOpen(false)}
                reportId={reportId}
                onCreateSuccess={handleCreateLinkSuccess}
                onPublishReport={onPublishReport}
            />

            <DeleteReportModal
                isOpen={deleteConfirmation.isOpen}
                reportName={deleteConfirmation.reportName}
                reportId={deleteConfirmation.reportId}
                onDeleteReport={onDeleteReport}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default ShareDropdown;
